import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Mail, ExternalLink } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Erectile Dysfunction', path: '/services/erectile-dysfunction' },
    { name: "Peyronie's Disease", path: '/services/peyronies-disease' },
    { name: 'Low Testosterone', path: '/services/low-testosterone' },
    { name: 'Enlarged Prostate (BPH)', path: '/services/bph' },
    { name: 'Vasectomy', path: '/services/vasectomy' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: "Men's Health", path: '/mens-health' },
    { name: 'About', path: '/about' },
    { name: 'New Patients', path: '/new-patients' },
    { name: 'Contact', path: '/contact' },
    { name: 'Accessibility', path: '/accessibility' },
  ];

  return (
    <footer className="bg-black text-white pb-20 md:pb-0" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <span className="font-heading text-xl font-bold">
                HAVKIN <span className="font-normal">UROLOGY</span>
              </span>
            </Link>
            <div className="space-y-4 text-gray-300">
              <a 
                href="tel:+13215004545" 
                className="flex items-start hover:text-white transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <span>(321) 500-4545</span>
              </a>
              <a 
                href="https://maps.google.com/?q=3021+W+Eau+Gallie+Blvd+Suite+103+Melbourne+FL+32934" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-start hover:text-white transition-colors"
                data-testid="footer-address"
              >
                <MapPin className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <span>3021 W Eau Gallie Blvd<br />Suite 103<br />Melbourne, FL 32934</span>
              </a>
              <div className="flex items-start text-gray-400">
                <Clock className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <span>Mon – Fri: 8:00 AM – 5:00 PM</span>
              </div>
              <a 
                href="mailto:drhavkin@havkinurology.com"
                className="flex items-start hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <span>drhavkin@havkinurology.com</span>
              </a>
              <a 
                href="https://www.instagram.com/havkinurology/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                <ExternalLink className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
                <span>@havkinurology</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path + link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.path.substring(1) || 'home'}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Services</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link 
                    to={service.path}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Ready to Schedule?</h4>
            <p className="text-gray-300 mb-6">
              Take the first step toward better health. Request an appointment today.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center px-6 py-3 bg-white text-black hover:bg-gray-200 rounded-full font-medium transition-colors"
              data-testid="footer-cta"
            >
              Request Appointment
            </Link>
          </div>
        </div>

        {/* Disclaimer & Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <p className="text-sm text-gray-400 mb-4" data-testid="footer-disclaimer">
            This website is for informational purposes only and does not provide medical advice. 
            If you believe you are experiencing a medical emergency, call 911.
          </p>
          <p className="text-sm text-gray-500">
            © {currentYear} Havkin Urology. All rights reserved. | Website Designed By{' '}
            <a 
              href="https://bp.agency" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-bold text-gray-400 hover:text-white transition-colors"
            >
              Brand Partners
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
